import React, { useState } from 'react';
import { Card, Button, Badge } from '@/components/ui';
import { Clock, Check, X } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { approveRegistration, rejectRegistration } from '@/services/tokenAdminService';

export interface PendingOrg {
  id: string;
  name: string;
  pendingContactEmail?: string;
  pendingContactName?: string;
  subscription?: { plan?: string };
}

interface Props {
  pending: PendingOrg[];
  onChanged: () => void;
}

/**
 * Top-of-page card listing orgs awaiting registration approval. Rendered only
 * when ≥1 org is pending. Approve starts the trial + grants tokens + sends the
 * held welcome email; Reject (two-click confirm) disables the auth user.
 */
export function PendingRegistrationsCard({ pending, onChanged }: Props) {
  const { success, error: toastError } = useToast();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmReject, setConfirmReject] = useState<string | null>(null);

  if (pending.length === 0) return null;

  const doApprove = async (id: string) => {
    setBusyId(id);
    try {
      await approveRegistration(id);
      success('Registration approved — trial started and welcome email sent');
      onChanged();
    } catch (e: any) {
      toastError(e?.message ?? 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async (id: string) => {
    setBusyId(id);
    try {
      await rejectRegistration(id);
      success('Registration rejected');
      setConfirmReject(null);
      onChanged();
    } catch (e: any) {
      toastError(e?.message ?? 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card padding="lg" className="border-status-pending/40">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-status-pending" />
        <h3 className="text-h3 font-semibold text-foreground">Pending registrations</h3>
        <Badge variant="warning">{pending.length}</Badge>
      </div>
      <div className="divide-y divide-border">
        {pending.map((org) => (
          <div key={org.id} className="flex items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate">{org.name}</div>
              <div className="text-sm text-muted-foreground truncate">
                {org.pendingContactName ? `${org.pendingContactName} · ` : ''}
                {org.pendingContactEmail ?? 'unknown contact'}
                {org.subscription?.plan ? ` · ${org.subscription.plan}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {confirmReject === org.id ? (
                <>
                  <Button size="sm" variant="destructive" disabled={busyId === org.id} onClick={() => doReject(org.id)}>
                    Confirm reject
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmReject(null)}>Cancel</Button>
                </>
              ) : (
                <>
                  <Button size="sm" disabled={busyId === org.id} onClick={() => doApprove(org.id)}>
                    <Check className="w-4 h-4 mr-1.5" />Approve
                  </Button>
                  <Button size="sm" variant="outline" disabled={busyId === org.id} onClick={() => setConfirmReject(org.id)}>
                    <X className="w-4 h-4 mr-1.5" />Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
